import type { OnboardingIntent, Persona } from "@/models/onboardingPersona";

export type PortableOnboardingStep =
  | "intent"
  | "persona"
  | "aha-demo"
  | "review"
  | "paywall";

export const portableOnboardingSteps: readonly PortableOnboardingStep[] = [
  "intent",
  "persona",
  "aha-demo",
  "review",
  "paywall",
];

export const getOnboardingSteps = (platform: "ios" | "android" | "web"): readonly PortableOnboardingStep[] =>
  platform === "ios" ? portableOnboardingSteps.filter((step) => step !== "review") : portableOnboardingSteps;

export const getNextOnboardingStepIndex = (
  index: number,
  count = portableOnboardingSteps.length,
): number => Math.min(Math.max(0, count - 1), index + 1);

export const getPreviousOnboardingStepIndex = (index: number): number =>
  Math.max(0, index - 1);

export const isOnboardingContinueDisabled = (
  step: PortableOnboardingStep | undefined,
  selections: {
    hasPersona: boolean;
    hasIntent: boolean;
  },
): boolean => {
  if (step === "persona") return !selections.hasPersona;
  if (step === "intent") return !selections.hasIntent;
  return false;
};

export const getOnboardingPurchaseEffect = (
  hasPackage: boolean,
): "purchase" | "retryPackages" => (hasPackage ? "purchase" : "retryPackages");

const marketingIntents: ReadonlySet<OnboardingIntent> = new Set([
  "import",
  "record",
  "dictate",
  "study",
  "translate",
]);

const marketingPersonas: ReadonlySet<Persona> = new Set([
  "business",
  "student",
  "content-creator",
  "medical",
  "lawyer",
  "coach-tutor",
  "it-specialist",
  "hearing-loss",
  "other",
]);

export type MarketingOnboardingContext = {
  intent: OnboardingIntent | null;
  persona: Persona | null;
};

export const UPLOAD_ENTRY = "upload";

export const isUploadEntry = (search: string): boolean =>
  new URLSearchParams(search).get("entry") === UPLOAD_ENTRY;

// Use cases an ad link can name (`&angle=`) so the upload prompt speaks to
// that file. Each needs its own subtitle in every locale; anything else falls
// back to the generic one.
export const UPLOAD_ANGLES = [
  "interview",
  "lecture",
  "meeting",
  "hearing",
  "sermon",
  "podcast",
] as const;

export type UploadAngle = (typeof UPLOAD_ANGLES)[number];

export const getUploadAngle = (search: string): UploadAngle | undefined => {
  const angle = new URLSearchParams(search).get("angle");
  return UPLOAD_ANGLES.find((known) => known === angle);
};

export const parseMarketingOnboardingContext = (
  search: string,
): MarketingOnboardingContext => {
  const params = new URLSearchParams(search);
  const intent = params.get("intent");
  const persona = params.get("persona");

  return {
    intent:
      intent && marketingIntents.has(intent as OnboardingIntent)
        ? (intent as OnboardingIntent)
        : null,
    persona:
      persona && marketingPersonas.has(persona as Persona)
        ? (persona as Persona)
        : null,
  };
};
